import React, { useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import {
  Forum as ForumIcon,
  Groups as GroupsIcon,
  MenuBook as MenuBookIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import UIBox from 'components/ui/UIBox';
import UIFlex from 'components/ui/UIFlex';
import UIText from 'components/ui/UIText';
import UIContainer from 'components/ui/UIContainer';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import PageIntro from 'components/PageIntro/PageIntro';
import WithLoader from 'components/WithLoader';
import { useAppSelector } from 'app/hooks';
import { WordSetCategoryType } from 'app/types';
import WordSetRow from './WordSetRow';
import LevelRow from './LevelRow';
import SourceRow, { FeaturedSet } from './SourceRow';
import CategoryRail, { RailItem } from './CategoryRail';

import thematicBg from 'assets/bg/thematic-lexicon.png';
import mostCommonBg from 'assets/bg/most-wide-words.png';
import irregularBg from 'assets/bg/irregular-verbs.png';
import phrasalBg from 'assets/bg/phraze-verbs.png';
import collocationsBg from 'assets/bg/words-combinations.png';
import idiomsBg from 'assets/bg/idioms.png';

// ── Curriculum data (family 1) ──────────────────────────────────────────────
// Three linguistic clusters that already exist in production. The `level` row
// is rendered through {@link LevelRow} (CEFR fan thumb); everything else uses
// {@link WordSetRow}.

interface CourseRowData {
  kind: 'row';
  id: WordSetCategoryType;
  title: string;
  en: string;
  desc: string;
  illustration: string;
  accent: string;
  units?: { category: string; sub: string; words: string };
}

interface LevelMarker {
  kind: 'level';
  id: 'level-based';
  title: string;
  accent: string;
}

interface CurriculumGroup {
  id: string;
  label: string;
  rows: Array<CourseRowData | LevelMarker>;
}

// Curriculum accent is the brand primary — keeps the existing 7 categories
// visually unified vs. the source-by-origin family (each has its own accent).
const CURRICULUM_ACCENT = '#3B2C93';

const CURRICULUM: CurriculumGroup[] = [
  {
    id: 'words',
    label: 'Слова',
    rows: [
      {
        kind: 'level',
        id: 'level-based',
        title: 'Англійська лексика за рівнями',
        accent: CURRICULUM_ACCENT,
      },
      {
        kind: 'row',
        id: 'most-common',
        title: 'Найпоширеніші слова',
        en: 'Most common words',
        desc: 'Ядро мови: слова, що покривають більшість повсякденного мовлення.',
        illustration: mostCommonBg,
        accent: CURRICULUM_ACCENT,
      },
      {
        kind: 'row',
        id: 'topic-related',
        title: 'Тематична лексика',
        en: 'Topical vocabulary',
        desc: 'Слова, згруповані за життєвими темами — обери будь-яку й вивчай у контексті.',
        illustration: thematicBg,
        accent: CURRICULUM_ACCENT,
      },
    ],
  },
  {
    id: 'verbs',
    label: 'Дієслова',
    rows: [
      {
        kind: 'row',
        id: 'irregular-verb',
        title: 'Неправильні дієслова',
        en: 'Irregular verbs',
        desc: 'Три форми найважливіших дієслів — таблиці й тренування вимови.',
        illustration: irregularBg,
        accent: CURRICULUM_ACCENT,
        units: { category: 'рівні', sub: 'уроків', words: 'слів' },
      },
    ],
  },
  {
    id: 'expr',
    label: 'Вирази',
    rows: [
      {
        kind: 'row',
        id: 'phrasal-verbs',
        title: 'Фразові дієслова',
        en: 'Phrasal verbs',
        desc: 'Дієслово + прийменник зі значенням, яке варто запам’ятати цілісно.',
        illustration: phrasalBg,
        accent: CURRICULUM_ACCENT,
      },
      {
        kind: 'row',
        id: 'collocations',
        title: 'Словосполучення',
        en: 'Collocations',
        desc: 'Природні поєднання слів, що звучать «як носій».',
        illustration: collocationsBg,
        accent: CURRICULUM_ACCENT,
      },
      {
        kind: 'row',
        id: 'idioms',
        title: 'Ідіоми та вирази',
        en: 'Idioms & expressions',
        desc: 'Образні вислови з переносним значенням для живого мовлення.',
        illustration: idiomsBg,
        accent: CURRICULUM_ACCENT,
      },
    ],
  },
];

// ── Source data (family 2 — coming-soon "Окремі набори") ────────────────────

type SourceData = {
  id: string;
  en: string;
  title: string;
  desc: string;
  accent: string;
  Icon: typeof MenuBookIcon;
  kind: 'books' | 'situations' | 'fields' | 'community';
  stats: Array<{ n: string; u: string }>;
  soon: true;
  eta: string;
  books?: string[];
  situations?: string[];
  fields?: string[];
  featured?: FeaturedSet[];
};

const SOURCES: SourceData[] = [
  {
    id: 'books',
    en: 'From Coursebooks',
    title: 'Набори з навчальних книг',
    desc: 'Лексика розділ-за-розділом із популярних підручників — навчайся синхронно з курсом.',
    accent: '#8E5CD9',
    Icon: MenuBookIcon,
    kind: 'books',
    stats: [
      { n: '9', u: 'книг' },
      { n: '214', u: 'наборів' },
      { n: '6 800', u: 'слів' },
    ],
    soon: true,
    eta: 'Липень 2026',
    books: ['Solutions', 'English File', 'Headway', 'Speakout', 'Outcomes'],
  },
  {
    id: 'practical',
    en: 'Practical Speech',
    title: 'Набори практичного мовлення',
    desc: 'Слова й фрази для реальних ситуацій — те, що справді знадобиться в розмові.',
    accent: '#E08A1E',
    Icon: ForumIcon,
    kind: 'situations',
    stats: [
      { n: '32', u: 'ситуації' },
      { n: '96', u: 'наборів' },
      { n: '1 900', u: 'слів' },
    ],
    soon: true,
    eta: 'Серпень 2026',
    situations: [
      'Ресторан',
      'Аеропорт',
      'Готель',
      'У лікаря',
      'Співбесіда',
      'Покупки',
      'Таксі',
      'Банк',
    ],
  },
  {
    id: 'professional',
    en: 'Professional',
    title: 'Професійна лексика',
    desc: 'Фахові терміни за галузями — від IT та медицини до фінансів і права.',
    accent: '#2A6FDB',
    Icon: WorkIcon,
    kind: 'fields',
    stats: [
      { n: '14', u: 'галузей' },
      { n: '120', u: 'наборів' },
      { n: '3 600', u: 'слів' },
    ],
    soon: true,
    eta: 'Осінь 2026',
    fields: [
      'IT та розробка',
      'Медицина',
      'Фінанси',
      'Право',
      'Маркетинг',
      'Інженерія',
      'HR',
      'Логістика',
    ],
  },
  {
    id: 'community',
    en: 'Community Sets',
    title: 'Набори слів користувачів',
    desc: 'Публічні набори від інших користувачів — знаходь, копіюй до себе й вивчай.',
    accent: '#FF7043',
    Icon: GroupsIcon,
    kind: 'community',
    stats: [
      { n: '1 240', u: 'наборів' },
      { n: '320', u: 'авторів' },
    ],
    soon: true,
    eta: 'Осінь 2026',
    featured: [
      // Save counts are placeholders until the community feature ships —
      // showing fake "4.2k" engagement on a coming-soon teaser felt
      // misleading, so each card starts at 0.
      { title: 'IELTS Academic 7+', author: 'Olha M.', words: 320, saves: '0' },
      { title: 'Business emails', author: 'Dmytro K.', words: 180, saves: '0' },
      { title: 'Tech & IT slang', author: 'Yana R.', words: 240, saves: '0' },
    ],
  },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

const pluralizeSetCount = (count: number): string => {
  if (count === 1) return 'набір';
  if (count < 5) return 'набори';
  return 'наборів';
};

const formatNumber = (n: number): string => n.toLocaleString('uk-UA');

const rowId = (row: CourseRowData | LevelMarker): string => row.id;

// ── Group head sub-component ────────────────────────────────────────────────

const GroupHead: React.FC<{ label: string; count: number }> = ({
  label,
  count,
}) => (
  <UIFlex ai={'center'} sx={{ flexWrap: 'nowrap', gap: 1.5, mb: 0.5 }}>
    <UIText
      sx={{
        color: 'primary.subtitle',
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </UIText>
    <UIText
      sx={{
        bgcolor: 'background.default',
        borderRadius: 999,
        color: 'primary.subtitle',
        fontSize: 12,
        fontWeight: 700,
        px: 1.1,
        py: 0.25,
        whiteSpace: 'nowrap',
      }}
    >
      {`${count} ${pluralizeSetCount(count)}`}
    </UIText>
    <UIBox sx={{ bgcolor: 'divider', flex: 1, height: '1px' }} />
  </UIFlex>
);

const SourceSectionHead: React.FC = () => (
  <UIFlex ai={'center'} sx={{ flexWrap: 'nowrap', gap: 1.5, mb: 0.5, mt: 1 }}>
    <UIText
      sx={{
        color: 'primary.subtitle',
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {'Окремі набори'}
    </UIText>
    <UIText
      sx={{
        bgcolor: (theme) =>
          theme.palette.mode === 'light'
            ? 'rgba(59,44,147,0.10)'
            : 'rgba(155,124,255,0.18)',
        borderRadius: 999,
        color: 'primary.main',
        fontSize: 11.5,
        fontWeight: 700,
        px: 1.125,
        py: 0.25,
        whiteSpace: 'nowrap',
      }}
    >
      {'незабаром'}
    </UIText>
    <UIBox sx={{ bgcolor: 'divider', flex: 1, height: '1px' }} />
  </UIFlex>
);

const OverviewStat: React.FC<{
  value: string;
  label: string;
  showDivider?: boolean;
}> = ({ value, label, showDivider }) => (
  <>
    {showDivider && (
      <UIBox
        sx={{
          bgcolor: 'divider',
          height: { xs: 'auto', sm: 32 },
          my: { xs: 0.25, sm: 0 },
          width: { xs: '1px', sm: '1px' },
        }}
      />
    )}
    <UIBox sx={{ flex: { xs: 1, sm: 'none' }, textAlign: 'center' }}>
      <UIText
        sx={{
          color: 'primary.main',
          fontSize: { xs: 20, md: 24 },
          fontWeight: 800,
          lineHeight: 1.1,
        }}
      >
        {value}
      </UIText>
      <UIText
        color={'primary.subtitle'}
        sx={{ fontSize: { xs: 12, md: 13 }, mt: 0.25 }}
      >
        {label}
      </UIText>
    </UIBox>
  </>
);

// ── Main page ───────────────────────────────────────────────────────────────

const WordSetPage: React.FC = () => {
  const loading = useAppSelector(({ common }) => common.wordSetData.loading);
  const allData = useAppSelector(({ common }) => common.wordSetData.data);
  // Lifted hover state so the left rail and right catalog rows can mirror
  // each other's hover. Either side sets it; both sides react.
  const [hoveredLeafId, setHoveredLeafId] = useState<string | null>(null);

  // Overview totals — same calculation as before, just driven by the new
  // CURRICULUM constant. The source family is intentionally excluded since
  // those categories have no real data yet.
  const overview = useMemo(() => {
    const totalSets = CURRICULUM.reduce(
      (acc, g) => acc + g.rows.length,
      0,
    );
    const totalThemes = allData.reduce(
      (acc, d) => acc + (d.categoryCount || 0),
      0,
    );
    const totalWords = allData.reduce(
      (acc, d) => acc + (d.wordsCount || 0),
      0,
    );
    return { totalSets, totalThemes, totalWords };
  }, [allData]);

  // Rail config — curriculum groups followed by 4 solo source items.
  const railItems = useMemo<RailItem[]>(() => {
    const groupItems: RailItem[] = CURRICULUM.map((group) => ({
      kind: 'group' as const,
      label: group.label,
      children: group.rows.map((row) => ({
        id: rowId(row),
        title:
          row.kind === 'level' ? row.title : (row as CourseRowData).title,
        accent:
          row.kind === 'level'
            ? row.accent
            : (row as CourseRowData).accent,
      })),
    }));
    const soloItems: RailItem[] = SOURCES.map((s) => ({
      kind: 'solo' as const,
      id: s.id,
      title: s.title,
      Icon: s.Icon,
      accent: s.accent,
      soon: s.soon,
    }));
    return [...groupItems, ...soloItems];
  }, []);

  return (
    <>
      <UIContainer>
        <BreadCrumbs />
      </UIContainer>

      <WithLoader loading={loading}>
        <UIContainer
          sx={{
            // Belt-and-braces: nothing inside the page is allowed to
            // produce horizontal scroll. Stat pill rows, the chip strip
            // and source-row preview clusters all wrap, but a stray
            // unwrapped node would otherwise drag the whole document
            // sideways on a narrow viewport. `overflow-x: clip` blocks
            // overflow without creating a scroll container (unlike
            // `hidden`), so descendant `position: sticky` keeps working.
            overflowX: 'clip',
            pb: 8,
          }}
        >
          <PageIntro
            surface
            title={'Набори слів'}
            titleSx={{ color: 'primary.main' }}
            description={
              'Структурований курс лексики від A1 до C2 — плюс набори з навчальних книг, практичного мовлення та спільноти.'
            }
            aside={
              <UIBox
                sx={{
                  display: 'flex',
                  flex: 'none',
                  gap: { xs: 1.25, sm: 2.5 },
                }}
              >
                <OverviewStat
                  value={String(overview.totalSets)}
                  label={pluralizeSetCount(overview.totalSets)}
                />
                <OverviewStat
                  value={formatNumber(overview.totalThemes)}
                  label={'тем'}
                  showDivider
                />
                <OverviewStat
                  value={formatNumber(overview.totalWords)}
                  label={'слів'}
                  showDivider
                />
              </UIBox>
            }
          />

          {/* Body: rail + catalog on desktop, single column on mobile */}
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 0, md: 4 },
              gridTemplateColumns: { xs: '1fr', md: '232px 1fr' },
            }}
          >
            <CategoryRail
              items={railItems}
              hoveredId={hoveredLeafId}
              onHover={setHoveredLeafId}
            />

            <Stack spacing={3.5}>
              {/* Curriculum family */}
              {CURRICULUM.map((group) => (
                <Stack key={group.id} spacing={1.5}>
                  <GroupHead label={group.label} count={group.rows.length} />
                  {group.rows.map((row) => (
                    <Box
                      key={row.id}
                      id={`ws-${row.id}`}
                      onMouseEnter={() => setHoveredLeafId(row.id)}
                      onMouseLeave={() => setHoveredLeafId(null)}
                      sx={{ scrollMarginTop: { xs: 128, md: 80 } }}
                    >
                      {row.kind === 'level' ? (
                        <LevelRow />
                      ) : (
                        <WordSetRow
                          id={row.id}
                          title={row.title}
                          en={row.en}
                          desc={row.desc}
                          illustration={row.illustration}
                          accent={row.accent}
                          units={row.units}
                        />
                      )}
                    </Box>
                  ))}
                </Stack>
              ))}

              {/* Source family — coming-soon section */}
              <Stack spacing={1.75}>
                <SourceSectionHead />
                {SOURCES.map((s) => (
                  <Box
                    key={s.id}
                    id={`ws-${s.id}`}
                    onMouseEnter={() => setHoveredLeafId(s.id)}
                    onMouseLeave={() => setHoveredLeafId(null)}
                    sx={{ scrollMarginTop: { xs: 128, md: 80 } }}
                  >
                    <SourceRow
                      id={s.id}
                      en={s.en}
                      title={s.title}
                      desc={s.desc}
                      accent={s.accent}
                      Icon={s.Icon}
                      kind={s.kind}
                      stats={s.stats}
                      soon={s.soon}
                      eta={s.eta}
                      books={s.books}
                      situations={s.situations}
                      fields={s.fields}
                      featured={s.featured}
                    />
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Box>
        </UIContainer>
      </WithLoader>
    </>
  );
};

export default WordSetPage;
